import { useEffect, useRef, useState, useMemo } from 'react';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import type { StormReport } from '@/hooks/useStormReports';
import { Loader2 } from 'lucide-react';

interface StormMapViewProps {
  reports: StormReport[];
  onSelectReport?: (r: StormReport) => void;
  height?: string;
}

type ViewMode = 'pins' | 'heatmap' | 'swaths';

// Color/size by hail size & type
function styleForReport(r: StormReport) {
  if (r.type === 'tornado') {
    return { color: '#a855f7', radius: 14, label: `EF${Math.round(r.magnitude)}` };
  }
  if (r.type === 'wind') {
    const mph = r.magnitude;
    const color = mph >= 80 ? '#dc2626' : mph >= 60 ? '#f97316' : '#fbbf24';
    return { color, radius: 8 + Math.min(mph / 20, 8), label: `${Math.round(mph)}mph` };
  }
  // hail
  const inches = r.magnitude;
  const color =
    inches >= 2 ? '#7f1d1d' :
    inches >= 1.5 ? '#dc2626' :
    inches >= 1 ? '#f97316' :
    inches >= 0.75 ? '#fbbf24' :
    '#22c55e';
  return { color, radius: 6 + inches * 6, label: `${inches.toFixed(2)}"` };
}

// Common hail-size descriptors
function hailDescriptor(inches: number): string {
  if (inches >= 4.5) return 'Softball';
  if (inches >= 4) return 'Grapefruit';
  if (inches >= 3) return 'Tea Cup';
  if (inches >= 2.75) return 'Baseball';
  if (inches >= 2.5) return 'Tennis Ball';
  if (inches >= 1.75) return 'Golf Ball';
  if (inches >= 1.5) return 'Ping Pong';
  if (inches >= 1.25) return 'Half Dollar';
  if (inches >= 1) return 'Quarter';
  if (inches >= 0.88) return 'Nickel';
  if (inches >= 0.75) return 'Penny';
  if (inches >= 0.5) return 'Marble';
  return 'Pea';
}

// Cluster hail reports by date + spatial proximity to form swath groups
function buildSwaths(reports: StormReport[]) {
  const hail = reports.filter(r => r.type === 'hail');
  // group by date
  const byDate = new Map<string, StormReport[]>();
  hail.forEach(r => {
    const arr = byDate.get(r.date) || [];
    arr.push(r);
    byDate.set(r.date, arr);
  });

  const swaths: { date: string; points: StormReport[]; maxHail: number }[] = [];
  for (const [date, items] of byDate) {
    // Simple greedy spatial clustering: ~0.6 deg (~40 mi) bin
    const visited = new Set<number>();
    items.forEach((seed, i) => {
      if (visited.has(i)) return;
      const cluster: StormReport[] = [seed];
      visited.add(i);
      for (let j = 0; j < items.length; j++) {
        if (visited.has(j)) continue;
        const c = items[j];
        // distance to any point in cluster
        const close = cluster.some(p => Math.abs(p.lat - c.lat) < 0.6 && Math.abs(p.lng - c.lng) < 0.6);
        if (close) {
          cluster.push(c);
          visited.add(j);
        }
      }
      if (cluster.length >= 1) {
        // sort by time to form path
        cluster.sort((a, b) => (a.time < b.time ? -1 : 1));
        swaths.push({
          date,
          points: cluster,
          maxHail: cluster.reduce((m, p) => Math.max(m, p.magnitude), 0),
        });
      }
    });
  }
  return swaths;
}

function swathColor(maxHail: number): string {
  if (maxHail >= 2) return '#7f1d1d';
  if (maxHail >= 1.5) return '#dc2626';
  if (maxHail >= 1) return '#f97316';
  if (maxHail >= 0.75) return '#fbbf24';
  return '#22c55e';
}

export function StormMapView({ reports, onSelectReport, height = '560px' }: StormMapViewProps) {
  const { isLoaded, loadError } = useGoogleMaps();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<Array<google.maps.Marker | google.maps.Circle | google.maps.Polyline | google.maps.Polygon>>([]);
  const heatmapRef = useRef<any>(null);
  const infoRef = useRef<google.maps.InfoWindow | null>(null);
  const [mode, setMode] = useState<ViewMode>('pins');

  const swaths = useMemo(() => buildSwaths(reports), [reports]);

  // init map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstance.current) return;
    mapInstance.current = new google.maps.Map(mapRef.current, {
      center: { lat: 39.5, lng: -98.35 },
      zoom: 4,
      mapTypeId: 'terrain',
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });
    infoRef.current = new google.maps.InfoWindow({ disableAutoPan: true });
  }, [isLoaded]);

  // render overlays based on mode
  useEffect(() => {
    if (!mapInstance.current || !isLoaded) return;
    const map = mapInstance.current;

    // clear everything
    overlaysRef.current.forEach(o => o.setMap(null));
    overlaysRef.current = [];
    if (heatmapRef.current) { heatmapRef.current.setMap(null); heatmapRef.current = null; }
    infoRef.current?.close();

    if (reports.length === 0) return;

    const showInfo = (pos: google.maps.LatLngLiteral, html: string) => {
      if (!infoRef.current) return;
      infoRef.current.setContent(html);
      infoRef.current.setPosition(pos);
      infoRef.current.open({ map });
    };

    if (mode === 'heatmap' && (google.maps as any).visualization) {
      const data = reports.map(r => ({
        location: new google.maps.LatLng(r.lat, r.lng),
        weight: r.type === 'hail' ? Math.max(0.5, r.magnitude) : 1,
      }));
      heatmapRef.current = new (google.maps as any).visualization.HeatmapLayer({
        data, radius: 35, opacity: 0.7,
      });
      heatmapRef.current.setMap(map);
    } else if (mode === 'swaths') {
      // Draw per-report footprint circles + path polyline per swath
      swaths.forEach(sw => {
        const color = swathColor(sw.maxHail);
        // path polyline (chronological)
        if (sw.points.length >= 2) {
          const path = sw.points.map(p => ({ lat: p.lat, lng: p.lng }));
          const poly = new google.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 0.9,
            strokeWeight: 4,
            map,
          });
          poly.addListener('mouseover', (e: google.maps.PolyMouseEvent) => {
            showInfo(
              { lat: e.latLng!.lat(), lng: e.latLng!.lng() },
              `<div style="font-family:system-ui;font-size:12px;line-height:1.4;min-width:160px">
                <div style="font-weight:600;margin-bottom:2px">Hail Swath · ${sw.date}</div>
                <div>Max hail: <b>${sw.maxHail.toFixed(2)}"</b> (${hailDescriptor(sw.maxHail)})</div>
                <div>${sw.points.length} reports along path</div>
              </div>`
            );
          });
          poly.addListener('mouseout', () => infoRef.current?.close());
          overlaysRef.current.push(poly);
        }
        // footprint circles per point (radius scales with hail size; ~0.5mi per inch)
        sw.points.forEach(p => {
          const radiusMeters = Math.max(800, p.magnitude * 1609 * 0.5);
          const circle = new google.maps.Circle({
            center: { lat: p.lat, lng: p.lng },
            radius: radiusMeters,
            strokeColor: color,
            strokeOpacity: 0.9,
            strokeWeight: 1.5,
            fillColor: color,
            fillOpacity: 0.22,
            map,
            clickable: true,
          });
          const tooltip = `<div style="font-family:system-ui;font-size:12px;line-height:1.4;min-width:170px">
            <div style="font-weight:600;margin-bottom:2px">${p.location || 'Hail report'}, ${p.state}</div>
            <div>Date: <b>${p.date}</b> ${p.time} UTC</div>
            <div>Hail: <b>${p.magnitude.toFixed(2)}"</b> · ${hailDescriptor(p.magnitude)}</div>
            <div style="color:#666">${p.county} County</div>
          </div>`;
          circle.addListener('mouseover', () => showInfo({ lat: p.lat, lng: p.lng }, tooltip));
          circle.addListener('mouseout', () => infoRef.current?.close());
          circle.addListener('click', () => onSelectReport?.(p));
          overlaysRef.current.push(circle);
        });
      });
    } else {
      // pins
      const bounds = new google.maps.LatLngBounds();
      reports.forEach(r => {
        const s = styleForReport(r);
        const marker = new google.maps.Marker({
          position: { lat: r.lat, lng: r.lng },
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: s.radius,
            fillColor: s.color,
            fillOpacity: 0.7,
            strokeColor: '#fff',
            strokeWeight: 1.5,
          },
          title: `${r.type.toUpperCase()} ${s.label} — ${r.location}, ${r.state} (${r.date})`,
        });
        const tooltip = `<div style="font-family:system-ui;font-size:12px;line-height:1.4;min-width:160px">
          <div style="font-weight:600;margin-bottom:2px">${r.location || r.type}, ${r.state}</div>
          <div>Date: <b>${r.date}</b> ${r.time} UTC</div>
          <div>${r.type === 'hail' ? `Hail: <b>${r.magnitude.toFixed(2)}"</b> · ${hailDescriptor(r.magnitude)}`
              : r.type === 'wind' ? `Wind: <b>${Math.round(r.magnitude)} mph</b>`
              : `Tornado: <b>EF${Math.round(r.magnitude)}</b>`}</div>
        </div>`;
        marker.addListener('mouseover', () => showInfo({ lat: r.lat, lng: r.lng }, tooltip));
        marker.addListener('mouseout', () => infoRef.current?.close());
        marker.addListener('click', () => onSelectReport?.(r));
        overlaysRef.current.push(marker);
        bounds.extend({ lat: r.lat, lng: r.lng });
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 60);
        const listener = google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
          if (map.getZoom()! > 10) map.setZoom(10);
        });
        return () => google.maps.event.removeListener(listener);
      }
    }

    // For swath mode, fit bounds too
    if (mode === 'swaths' && reports.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      reports.forEach(r => bounds.extend({ lat: r.lat, lng: r.lng }));
      map.fitBounds(bounds, 60);
    }
  }, [reports, isLoaded, mode, swaths, onSelectReport]);

  if (loadError) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-sm text-destructive" style={{ height }}>
        Failed to load Google Maps
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden border border-border" style={{ height }}>
      {!isLoaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />

      {/* Legend */}
      <div className="absolute top-3 left-3 z-10 rounded-lg border border-border bg-background/95 backdrop-blur p-3 text-xs shadow-lg space-y-1.5 max-w-[220px]">
        <div className="font-semibold text-foreground mb-1">
          {mode === 'swaths' ? 'Hail Swath Severity' : 'Hail Size'}
        </div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: '#7f1d1d' }} />2"+ Baseball+</div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: '#dc2626' }} />1.5" Egg</div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: '#f97316' }} />1" Quarter</div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: '#fbbf24' }} />0.75" Penny</div>
        {mode !== 'swaths' && (
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: '#a855f7' }} />Tornado</div>
        )}
        {mode === 'swaths' && (
          <div className="pt-1 mt-1 border-t border-border text-[11px] text-muted-foreground">
            {swaths.length} swath{swaths.length === 1 ? '' : 's'} · hover for details
          </div>
        )}
      </div>

      {/* Mode toggle */}
      <div className="absolute top-3 right-3 z-10 rounded-lg border border-border bg-background/95 backdrop-blur p-1 text-xs shadow-lg flex gap-0.5">
        {(['pins', 'swaths', 'heatmap'] as ViewMode[]).map(m => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-2.5 py-1.5 rounded font-medium capitalize transition ${
              mode === m ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
            }`}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );
}
